import type { UserActivityDataResponse, UserGrowthDataResponse } from "../types/dashboard-data.types";

export const DASHBOARD_CACHE_TTL_MS = 10 * 60 * 1000;

export const DASHBOARD_USER_GROWTH_CACHE_KEY = "dashboard_user_growth";
export const DASHBOARD_USER_ACTIVITY_CACHE_KEY = "dashboard_user_activity";

export type DashboardCacheKey =
  | typeof DASHBOARD_USER_GROWTH_CACHE_KEY
  | typeof DASHBOARD_USER_ACTIVITY_CACHE_KEY;

export type DashboardCachedValue<TKey extends DashboardCacheKey> =
  TKey extends typeof DASHBOARD_USER_GROWTH_CACHE_KEY ? UserGrowthDataResponse : UserActivityDataResponse;

interface DashboardCacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_KEYS: readonly DashboardCacheKey[] = [
  DASHBOARD_USER_GROWTH_CACHE_KEY,
  DASHBOARD_USER_ACTIVITY_CACHE_KEY,
];

const isCacheKey = (key: string): key is DashboardCacheKey =>
  (CACHE_KEYS as readonly string[]).includes(key);

const getStorage = (): Storage | null => {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage;
  } catch {
    return null;
  }
};

const isFresh = (timestamp: number, now: number): boolean =>
  now - timestamp < DASHBOARD_CACHE_TTL_MS;

export const readDashboardCache = <TKey extends DashboardCacheKey>(
  key: TKey,
): DashboardCachedValue<TKey> | null => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as Partial<DashboardCacheEntry<DashboardCachedValue<TKey>>>;
    if (!entry || typeof entry.timestamp !== "number" || !("data" in entry)) {
      storage.removeItem(key);
      return null;
    }
    if (!isFresh(entry.timestamp, Date.now())) {
      storage.removeItem(key);
      return null;
    }
    return entry.data as DashboardCachedValue<TKey>;
  } catch {
    try {
      storage.removeItem(key);
    } catch {
      // Ignore cleanup failures; a missing cache simply refetches.
    }
    return null;
  }
};

export const writeDashboardCache = <TKey extends DashboardCacheKey>(
  key: TKey,
  data: DashboardCachedValue<TKey>,
): void => {
  const storage = getStorage();
  if (!storage) return;

  try {
    const entry: DashboardCacheEntry<DashboardCachedValue<TKey>> = {
      data,
      timestamp: Date.now(),
    };
    storage.setItem(key, JSON.stringify(entry));
  } catch {
    // Caching is best-effort; fetch results remain usable without it.
  }
};

export const invalidateDashboardCache = (key?: DashboardCacheKey): void => {
  const storage = getStorage();
  if (!storage) return;

  try {
    if (key) {
      storage.removeItem(key);
      return;
    }
    CACHE_KEYS.forEach((cacheKey) => storage.removeItem(cacheKey));
  } catch {
    // Ignore cleanup failures; stale entries expire via TTL.
  }
};

export const clearDashboardCache = (): void => {
  invalidateDashboardCache();
};

export const isDashboardCacheKey = isCacheKey;
